import { sortExpr } from './sort'
import { projectId as toProjectId, userId as toUserId } from '@thom/folio-domain/core/domain/project'
import { planId as toPlanId } from '@thom/folio-domain/core/domain/plan'
import { issueId as toIssueId } from '@thom/folio-domain/core/domain/issue'
import type { Entry, EntryKind } from '@thom/folio-domain/core/domain/entry'
import { entryId as toEntryId } from '@thom/folio-domain/core/domain/entry'
import type { EntriesPort, EntryFilter } from '@thom/folio-domain/core/ports/entries'
import { err, ok, type Result } from '@thom/folio-domain/lib/result'
import { tryCatch } from '@thom/folio-domain/lib/try-catch'
import { Collections, type JournEntriesResponse } from '@thom/folio-ui/pocketbase-types'
import { getPocketBaseClient } from './client'
import { keyed } from './request-key'
import { countRows } from './count-rows'
import { filterFor } from '@thom/pb/filter-builder'
import { projectClauses } from './project-clauses'
import { paginate } from './paginate'
import { tagsByTarget } from './relations'

type EntryRecord = JournEntriesResponse<unknown, string[]>

type EntryColumns = {
	id: string
	'project.slug': string
	'project.domain.slug': string
	'project.domain.client.slug': string
	kind: EntryKind
	issue: string
	plan: string
	branch: string
	external_ref: string
	slug: string
	title: string
	body: string
	created: Date
	updated: Date
}

const toEntry = (record: EntryRecord): Entry => ({
	id: toEntryId(record.id),
	kind: record.kind as EntryKind,
	projectId: toProjectId(record.project),
	issueId: record.issue ? toIssueId(record.issue) : undefined,
	planId: record.plan ? toPlanId(record.plan) : undefined,
	slug: record.slug ?? '',
	title: record.title ?? '',
	body: record.body ?? '',
	branch: record.branch ?? '',
	pr: record.pr ?? '',
	externalRef: record.external_ref ?? '',
	tags: [],
	createdBy: record.created_by ? toUserId(record.created_by) : undefined,
	createdAt: new Date(record.created),
	updatedAt: new Date(record.updated),
})

const withTags = async (entries: readonly Entry[]): Promise<readonly Entry[]> => {
	if (entries.length === 0) return entries

	const tags = await tagsByTarget(
		'entry',
		entries.map(entry => entry.id as string),
	)

	return entries.map(entry => ({ ...entry, tags: tags.get(entry.id as string) ?? [] }))
}

const withTagsOne = async (entry: Entry): Promise<Entry> => {
	const [hydrated] = await withTags([entry])
	return hydrated ?? entry
}

const columns = (project: string, filter: EntryFilter) =>
	filterFor<EntryColumns>()([
		...projectClauses(project),
		{ field: 'id', comparator: 'eq', value: filter.id },
		{ field: 'kind', comparator: 'eq', value: filter.kind },
		{ field: 'kind', comparator: 'anyOf', value: filter.kinds },
		{ field: 'issue', comparator: 'eq', value: filter.issueId },
		{ field: 'plan', comparator: 'eq', value: filter.planId },
		{ field: 'branch', comparator: 'eq', value: filter.branch },
		{ field: 'external_ref', comparator: 'eq', value: filter.externalRef },
		{ field: 'title', comparator: 'contains', value: filter.search },
	])

const matchesTags = (entry: Entry, tags: readonly string[] | undefined): boolean => {
	if (!tags || tags.length === 0) return true
	const have = new Set(entry.tags.map(tag => tag.toLowerCase()))
	return tags.every(tag => have.has(tag.toLowerCase()))
}

const createEntriesAdapter = (): EntriesPort => {
	const client = getPocketBaseClient()
	const collection = () => client.collection(Collections.JournEntries)

	return {
		count: async (project, filter = {}): Promise<Result<number>> => {
			const { expr, params } = columns(project, filter)

			const { data, error } = await tryCatch(
				countRows(collection(), keyed('entries.count', { filter: client.filter(expr, params) })),
			)

			return error ? err(new Error(`Failed to count entries: ${error.message}`, { cause: error })) : ok(data)
		},

		list: async (project, filter = {}): Promise<Result<readonly Entry[]>> => {
			const { expr, params } = columns(project, filter)

			const { data, error } = await tryCatch(
				paginate<EntryRecord>(
					collection(),
					filter,
					keyed(
						'entries.list',
						{
							filter: client.filter(expr, params),
							sort: sortExpr(filter.sort, '-created'),
						},
						[filter.limit, filter.offset, filter.tags],
					),
				),
			)
			if (error) return err(new Error(`Failed to list entries: ${error.message}`, { cause: error }))

			const hydrated = await tryCatch(withTags(data.map(toEntry)))
			if (hydrated.error) {
				return err(new Error(`Failed to load entry tags: ${hydrated.error.message}`, { cause: hydrated.error }))
			}

			return ok(hydrated.data.filter(entry => matchesTags(entry, filter.tags)))
		},

		get: async (project, slug): Promise<Result<Entry>> => {
			const { expr, params } = filterFor<EntryColumns>()([
				...projectClauses(project),
				{ field: 'slug', comparator: 'eq', value: slug },
			])

			const { data, error } = await tryCatch(
				collection().getFirstListItem<EntryRecord>(client.filter(expr, params), keyed('entries.get', {})),
			)
			if (error) return err(new Error(`Failed to load entry ${slug}: ${error.message}`, { cause: error }))

			const hydrated = await tryCatch(withTagsOne(toEntry(data)))
			return hydrated.error
				? err(new Error(`Failed to load entry tags: ${hydrated.error.message}`, { cause: hydrated.error }))
				: ok(hydrated.data)
		},

		getById: async (project, id): Promise<Result<Entry>> => {
			const { expr, params } = filterFor<EntryColumns>()([...projectClauses(project)])

			const { data, error } = await tryCatch(
				collection().getOne<EntryRecord>(id, keyed(`entries.getById.${id}`, { filter: client.filter(expr, params) })),
			)
			if (error) return err(new Error(`Failed to load entry ${id}: ${error.message}`, { cause: error }))

			const hydrated = await tryCatch(withTagsOne(toEntry(data)))
			return hydrated.error
				? err(new Error(`Failed to load entry tags: ${hydrated.error.message}`, { cause: hydrated.error }))
				: ok(hydrated.data)
		},
	}
}

export { columns, createEntriesAdapter }
